import { useState, type ButtonHTMLAttributes, type CSSProperties } from "react";
import { useEmbedTokens } from "../theme";
import { EmbedSpinner } from "./EmbedSpinner";

type Variant = "primary" | "secondary" | "ghost" | "danger";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  variant?: Variant;
  size?: "md" | "sm";
  loading?: boolean;
  fullWidth?: boolean;
}

export function EmbedButton({ variant = "primary", size = "md", loading = false, fullWidth = false, disabled, onMouseEnter, onMouseLeave, children, ...rest }: Props) {
  const t = useEmbedTokens();
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;
  const base: CSSProperties = {
    fontFamily: "inherit", fontSize: size === "sm" ? t.fontSize.xs : t.fontSize.sm, fontWeight: 600,
    height: size === "sm" ? 34 : t.buttonHeight, padding: size === "sm" ? "0 12px" : "0 18px",
    borderRadius: t.borderRadius, border: "1px solid transparent", cursor: isDisabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    width: fullWidth ? "100%" : undefined, transition: "background .12s,border-color .12s,color .12s",
  };
  const byVariant: Record<Variant, CSSProperties> = {
    primary: { background: t.colors.primary, color: t.colors.white },
    secondary: { background: "transparent", color: t.colors.text, borderColor: t.colors.border },
    ghost: { background: "transparent", color: t.colors.primary },
    danger: { background: "transparent", color: t.colors.error, borderColor: t.colors.error },
  };
  const hoverByVariant: Record<Variant, CSSProperties> = {
    primary: { background: t.colors.primaryHover },
    secondary: { background: t.colors.primaryTint },
    ghost: { background: t.colors.primaryTint },
    danger: { background: t.colors.errorBg, borderColor: t.colors.error },
  };
  const disabledStyle: CSSProperties = { background: t.colors.disabledBg, color: t.colors.disabled, borderColor: "transparent" };
  const variantStyle = isDisabled ? disabledStyle : { ...byVariant[variant], ...(hovered ? hoverByVariant[variant] : {}) };
  return (
    <button {...rest} disabled={isDisabled}
      onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e); }}
      style={{ ...base, ...variantStyle }}>
      {loading && <EmbedSpinner size={16} onColor={variant === "primary"} />}
      {children}
    </button>
  );
}
